import React, { useState, useEffect } from 'react';
import { X, Eye, EyeOff, Briefcase, User, Loader, KeyRound, CheckCircle, ShieldCheck, ChevronRight } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { forgotPassword } from '../services/api';
import { overlayDismiss } from '../utils/overlayDismiss';
import './AuthModal.css';

// Clientseitige Passwort-Checks (nur UX — massgeblich ist die Serverprüfung)
export const PASSWORD_RULES = [
  { label: 'Mindestens 8 Zeichen', test: pw => pw.length >= 8 },
  { label: 'Ein Grossbuchstabe', test: pw => /[A-Z]/.test(pw) },
  { label: 'Ein Kleinbuchstabe', test: pw => /[a-z]/.test(pw) },
  { label: 'Eine Zahl', test: pw => /[0-9]/.test(pw) },
  { label: 'Ein Sonderzeichen', test: pw => /[^A-Za-z0-9]/.test(pw) },
];

export default function AuthModal() {
  const {
    showAuthModal, setShowAuthModal,
    authMode, setAuthMode,
    login, confirm2FA, register,
    authLoading, authError, setAuthError,
    setPendingVerificationEmail,
  } = useApp();

  const [userType, setUserType] = useState(null);
  // Registrierung startet immer mit der Rollenwahl
  const [regStep, setRegStep] = useState('role'); // role | form
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', companyName: '', email: '', password: '' });
  const [agbAccepted, setAgbAccepted] = useState(false);
  const [forgotMode, setForgotMode] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotLoading, setForgotLoading] = useState(false);
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotError, setForgotError] = useState(null);
  const [twoFaMode, setTwoFaMode] = useState(false);
  const [twoFaCode, setTwoFaCode] = useState('');

  useEffect(() => {
    if (showAuthModal) {
      setRegStep('role');
      setUserType(null);
    }
  }, [showAuthModal]);

  if (!showAuthModal) return null;

  const isRegister = authMode === 'register';
  const pwValid = PASSWORD_RULES.every(r => r.test(form.password));

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (isRegister) {
        const firstName = userType === 'employer' ? form.companyName : form.firstName;
        const lastName = userType === 'employer' ? '' : form.lastName;
        await register(firstName, lastName, form.email, form.password, userType);
      } else {
        const { twoFactorRequired, verificationRequired } = await login(form.email, form.password);
        if (verificationRequired) {
          // Konto noch nicht bestätigt → Code-Eingabe-Screen (bestätigt + meldet an)
          setShowAuthModal(false);
          setPendingVerificationEmail(form.email);
        } else if (twoFactorRequired) {
          setTwoFaCode('');
          setTwoFaMode(true);
        }
      }
    } catch {
      // error is set in context
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    try {
      await confirm2FA(form.email, twoFaCode);
      setTwoFaMode(false);
    } catch {
      // error is set in context
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    setForgotError(null);
    try {
      await forgotPassword(forgotEmail);
      setForgotSent(true);
    } catch (err) {
      setForgotError(err.response?.data?.error || err.response?.data?.message || 'Ein Fehler ist aufgetreten. Bitte versuche es erneut.');
    } finally {
      setForgotLoading(false);
    }
  };

  const switchMode = (mode) => {
    setAuthMode(mode);
    setAuthError(null);
    setForgotMode(false);
    setForgotSent(false);
    setForgotError(null);
    setAgbAccepted(false);
    setTwoFaMode(false);
    setTwoFaCode('');
    setForm({ firstName: '', lastName: '', companyName: '', email: '', password: '' });
  };

  const openForgot = () => {
    setForgotMode(true);
    setForgotEmail(form.email);
    setForgotSent(false);
    setForgotError(null);
  };

  const closeForgot = () => {
    setForgotMode(false);
    setForgotSent(false);
    setForgotError(null);
  };

  /* ── 2FA-Code ── */
  if (twoFaMode) {
    return (
      <div className="modal-overlay animate-fadeIn" {...overlayDismiss(() => setShowAuthModal(false))}>
        <div className="auth-modal animate-fadeInUp">
          <button className="auth-modal__close" onClick={() => setShowAuthModal(false)}>
            <X size={20} />
          </button>

          <div className="auth-modal__header">
            <div className="auth-modal__logo"><ShieldCheck size={20} /></div>
            <h2>Sicherheitscode</h2>
            <p>Wir haben dir einen 6-stelligen Code an <strong>{form.email}</strong> gesendet.</p>
          </div>

          {authError && <div className="auth-modal__error">{authError}</div>}

          <form className="auth-modal__form" onSubmit={handle2FASubmit}>
            <div className="form-group">
              <label>Code aus der E-Mail</label>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="[0-9]{6}"
                maxLength={6}
                placeholder="123456"
                value={twoFaCode}
                onChange={e => setTwoFaCode(e.target.value.replace(/\D/g, ''))}
                required
                disabled={authLoading}
                autoFocus
                style={{ textAlign: 'center', letterSpacing: '8px', fontSize: '20px', fontWeight: 700 }}
              />
            </div>
            <button type="submit" className="auth-modal__submit" disabled={authLoading || twoFaCode.length !== 6}>
              {authLoading
                ? <><Loader size={16} className="spin" /> Prüfen...</>
                : 'Anmeldung bestätigen'}
            </button>
          </form>

          <p className="auth-modal__switch">
            <button onClick={() => { setTwoFaMode(false); setAuthError(null); }}>← Zurück zum Login</button>
          </p>
        </div>
      </div>
    );
  }

  /* ── Passwort vergessen ── */
  if (forgotMode) {
    return (
      <div className="modal-overlay animate-fadeIn" {...overlayDismiss(() => setShowAuthModal(false))}>
        <div className="auth-modal animate-fadeInUp">
          <button className="auth-modal__close" onClick={() => setShowAuthModal(false)}>
            <X size={20} />
          </button>

          <div className="auth-modal__header">
            <div className="auth-modal__logo"><KeyRound size={20} /></div>
            <h2>Passwort zurücksetzen</h2>
            <p>Wir senden dir einen Reset-Link per E-Mail.</p>
          </div>

          {forgotSent ? (
            <div className="auth-modal__success auth-modal__success--lg">
              <CheckCircle size={16} /> Wir haben dir eine E-Mail geschickt. Bitte überprüfe deinen Posteingang (auch Spam).
            </div>
          ) : (
            <>
              {forgotError && <div className="auth-modal__error">{forgotError}</div>}
              <form className="auth-modal__form" onSubmit={handleForgot}>
                <div className="form-group">
                  <label>E-Mail-Adresse</label>
                  <input
                    type="email"
                    placeholder="max@beispiel.de"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    required
                    disabled={forgotLoading}
                    autoFocus
                  />
                </div>
                <button type="submit" className="auth-modal__submit" disabled={forgotLoading}>
                  {forgotLoading
                    ? <><Loader size={16} className="spin" /> Senden...</>
                    : 'Reset-Link anfordern'}
                </button>
              </form>
            </>
          )}

          <p className="auth-modal__switch">
            <button onClick={closeForgot}>← Zurück zum Login</button>
          </p>
        </div>
      </div>
    );
  }

  /* ── Registrierung Schritt 1: Rollenwahl ── */
  if (isRegister && regStep === 'role') {
    return (
      <div className="modal-overlay animate-fadeIn" {...overlayDismiss(() => setShowAuthModal(false))}>
        <div className="auth-modal animate-fadeInUp">
          <button className="auth-modal__close" onClick={() => setShowAuthModal(false)}>
            <X size={20} />
          </button>

          <div className="auth-modal__header">
            <div className="auth-modal__logo"><Briefcase size={20} /></div>
            <h2>Wie möchtest du LetsCareers nutzen?</h2>
            <p>Wähle zuerst deine Rolle – danach geht's zur Registrierung.</p>
          </div>

          <div className="auth-modal__role-list">
            <button
              className="auth-modal__role-card"
              onClick={() => { setUserType('youth'); setRegStep('form'); }}
            >
              <div className="auth-modal__role-icon"><User size={22} /></div>
              <div className="auth-modal__role-text">
                <strong>Ich suche einen Job</strong>
                <span>Jobs entdecken, speichern und dich direkt bewerben</span>
              </div>
              <ChevronRight size={18} />
            </button>
            <button
              className="auth-modal__role-card"
              onClick={() => { setUserType('employer'); setRegStep('form'); }}
            >
              <div className="auth-modal__role-icon"><Briefcase size={22} /></div>
              <div className="auth-modal__role-text">
                <strong>Ich bin Arbeitgeber</strong>
                <span>Inserate aufgeben und Talente finden</span>
              </div>
              <ChevronRight size={18} />
            </button>
          </div>

          <p className="auth-modal__switch">
            Bereits registriert?{' '}
            <button onClick={() => switchMode('login')}>Anmelden</button>
          </p>
        </div>
      </div>
    );
  }

  /* ── Login / Register ── */
  return (
    <div className="modal-overlay animate-fadeIn" {...overlayDismiss(() => setShowAuthModal(false))}>
      <div className="auth-modal animate-fadeInUp">
        <button className="auth-modal__close" onClick={() => setShowAuthModal(false)}>
          <X size={20} />
        </button>

        <div className="auth-modal__header">
          <div className="auth-modal__logo"><Briefcase size={20} /></div>
          <h2>{isRegister ? 'Konto erstellen' : 'Willkommen zurück'}</h2>
          <p>{isRegister ? 'Starte deine Karriere noch heute.' : 'Melde dich an, um fortzufahren.'}</p>
        </div>

        {isRegister && (
          <div className="auth-modal__role-chosen">
            {userType === 'employer'
              ? <><Briefcase size={15} /> Arbeitgeber</>
              : <><User size={15} /> Jobsuchend</>}
            <button type="button" onClick={() => setRegStep('role')}>Ändern</button>
          </div>
        )}

        {authError && <div className="auth-modal__error">{authError}</div>}

        <form className="auth-modal__form" onSubmit={handleSubmit}>
          {isRegister && userType === 'employer' && (
            <div className="form-group">
              <label>Firmenname</label>
              <input
                type="text"
                placeholder="Musterfirma GmbH"
                value={form.companyName}
                onChange={e => setForm({ ...form, companyName: e.target.value })}
                required
                disabled={authLoading}
              />
            </div>
          )}
          {isRegister && userType === 'youth' && (
            <>
              <div className="form-group">
                <label>Vorname</label>
                <input
                  type="text"
                  placeholder="Max"
                  value={form.firstName}
                  onChange={e => setForm({ ...form, firstName: e.target.value })}
                  required
                  disabled={authLoading}
                />
              </div>
              <div className="form-group">
                <label>Nachname</label>
                <input
                  type="text"
                  placeholder="Muster"
                  value={form.lastName}
                  onChange={e => setForm({ ...form, lastName: e.target.value })}
                  required
                  disabled={authLoading}
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label>E-Mail</label>
            <input
              type="email"
              placeholder="max@beispiel.de"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              required
              disabled={authLoading}
            />
          </div>

          <div className="form-group">
            <label>Passwort</label>
            <div className="input-with-icon">
              <input
                type={showPw ? 'text' : 'password'}
                placeholder="Mindestens 8 Zeichen"
                value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })}
                required
                disabled={authLoading}
              />
              <button type="button" className="input-icon-btn" onClick={() => setShowPw(!showPw)}>
                {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {isRegister && form.password.length > 0 && !pwValid && (
              <ul className="auth-modal__pw-rules">
                {PASSWORD_RULES.map(r => (
                  <li key={r.label} className={r.test(form.password) ? 'ok' : ''}>{r.label}</li>
                ))}
              </ul>
            )}
          </div>

          {!isRegister && (
            <div className="auth-modal__forgot">
              <button type="button" onClick={openForgot}>Passwort vergessen?</button>
            </div>
          )}

          {isRegister && (
            <label className="auth-modal__agb">
              <input
                type="checkbox"
                checked={agbAccepted}
                onChange={e => setAgbAccepted(e.target.checked)}
              />
              <span>
                Ich stimme den{' '}
                <a href="/agb" target="_blank" rel="noopener noreferrer">AGB</a>
                {' '}und der{' '}
                <a href="/datenschutz" target="_blank" rel="noopener noreferrer">Datenschutzerklärung</a>
                {' '}zu.
              </span>
            </label>
          )}

          <button
            type="submit"
            className="auth-modal__submit"
            disabled={authLoading || (isRegister && (!agbAccepted || !pwValid))}
          >
            {authLoading
              ? <><Loader size={16} className="spin" /> Bitte warten...</>
              : isRegister ? 'Konto erstellen' : 'Anmelden'}
          </button>
        </form>

        <p className="auth-modal__switch">
          {isRegister ? 'Bereits registriert? ' : 'Noch kein Konto? '}
          <button onClick={() => switchMode(isRegister ? 'login' : 'register')}>
            {isRegister ? 'Anmelden' : 'Jetzt registrieren'}
          </button>
        </p>
      </div>
    </div>
  );
}
